import { useEffect, useState } from 'react';
import { CloudOff } from 'lucide-react';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { cn } from '@/lib/utils';
import { useTaskStore } from '@/stores/taskStore';

/**
 * How long the live socket may be down before it says so: reconnects after a deploy, a network blip
 * or a renewed sign-in take a few seconds, and shouldn't flash anything up.
 */
export const LIVE_UPDATES_GRACE_MS = 20_000;

const HINT =
  "Live updates are paused while Software Factory reconnects: changes other people make, and the end of a sync, won't show here until it's back. What you do still saves.";

/** The chip itself, for the top bar and the /dev/ui gallery. */
export function LiveUpdatesPausedChip({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-live="polite"
      aria-label="Live updates paused, reconnecting"
      data-hint={HINT}
      className={cn(
        'inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-warning-border bg-warning-soft px-2 text-xs font-medium text-warning-strong',
        className
      )}
    >
      <CloudOff className="size-3.5 shrink-0" aria-hidden />
      <span className="max-sm:hidden">Reconnecting</span>
    </span>
  );
}

/**
 * Says so in the top bar when the live task hub's socket (TaskLiveConnector) has been down for
 * LIVE_UPDATES_GRACE_MS: pages then don't hear about other people's changes or a sync ending.
 * Nothing while the browser is offline: OfflineNotice covers that.
 */
export function LiveUpdatesIndicator() {
  const downSince = useTaskStore((s) => s.disconnectedAt);
  const online = useOnlineStatus();
  // The outage the grace timer has run out for; compared with downSince, so a new one starts over.
  const [overdueFor, setOverdueFor] = useState<number | null>(null);

  useEffect(() => {
    if (downSince === null) return undefined;
    const timer = setTimeout(
      () => setOverdueFor(downSince),
      Math.max(0, downSince + LIVE_UPDATES_GRACE_MS - Date.now())
    );
    return () => clearTimeout(timer);
  }, [downSince]);

  if (!online || downSince === null || overdueFor !== downSince) return null;
  return <LiveUpdatesPausedChip />;
}
